import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Loader, ButtonSpinner } from '../components/ui/Loader';
import {
  useGetPermanentDurationsByServerQuery,
  useGetPermanentLocationsByServerQuery,
  useGetPermanentPurchaseSummaryMutation,
  useGetPermanentServicesByServerQuery,
  usePurchasePermanentNumberMutation,
} from '../store/api/permanentNumbersApi';
import { PermanentNumber } from '../types/api';

const SERVER_ID_TEXT_VERIFIED = 1;

export function PermanentNumbersPurchasePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedServiceId, setSelectedServiceId] = useState<string>('');
  const [selectedAreaCode, setSelectedAreaCode] = useState<string>('');
  const [selectedDurationKey, setSelectedDurationKey] = useState<string>('');
  const [autoRenewEnabled] = useState<boolean>(false);
  const [capability, setCapability] = useState<string>('sms');
  const [summary, setSummary] = useState<{
    serviceName: string;
    areaCode?: string;
    autoRenewEnabled?: boolean;
    durationLabel?: string;
    durationDays?: number;
    price: number;
    currency: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [serviceSearchQuery, setServiceSearchQuery] = useState<string>('');
  const [stateSearchQuery, setStateSearchQuery] = useState<string>('');

  const {
    data: tvServicesData,
    isLoading: loadingTvServices,
    error: tvServicesError,
  } = useGetPermanentServicesByServerQuery({ serverId: SERVER_ID_TEXT_VERIFIED });

  const {
    data: tvLocationsData,
    isLoading: loadingTvLocations,
    error: tvLocationsError,
  } = useGetPermanentLocationsByServerQuery({ serverId: SERVER_ID_TEXT_VERIFIED });

  const { data: durationsData, isLoading: loadingDurations } = useGetPermanentDurationsByServerQuery(
    { serverId: SERVER_ID_TEXT_VERIFIED, autoRenewEnabled },
  );

  const [purchasePermanentNumber, { isLoading: isPurchasing }] =
    usePurchasePermanentNumberMutation();
  const [getPermanentPurchaseSummary, { isLoading: loadingSummary }] =
    useGetPermanentPurchaseSummaryMutation();

  const services = useMemo(() => {
    const allServices = tvServicesData?.data.services || [];

    if (!serviceSearchQuery.trim()) {
      return allServices;
    }

    const query = serviceSearchQuery.toLowerCase();
    return allServices.filter((service) => service.name.toLowerCase().includes(query));
  }, [tvServicesData, serviceSearchQuery]);

  const states = useMemo(() => {
    const normalized = (tvLocationsData?.data.locations || []).map((loc) => ({
      key: loc.id,
      value: loc.areaCode || '',
      label: loc.name,
      secondary: loc.areaCode || '',
    }));

    if (!stateSearchQuery.trim()) return normalized;

    const query = stateSearchQuery.toLowerCase();
    return normalized.filter(
      (state) =>
        state.label.toLowerCase().includes(query) ||
        state.secondary.toLowerCase().includes(query)
    );
  }, [tvLocationsData, stateSearchQuery]);

  const durationOptions = durationsData?.data.durations || [];

  const canShowPurchasePanel = Boolean(selectedServiceId);

  const isSummaryStage = summary !== null;

  const handlePurchase = async () => {
    setError(null);

    if (!selectedServiceId) {
      setError(t('permanentNumbers.purchase.errors.serviceRequired'));
      return;
    }
    if (!selectedDurationKey) {
      setError(t('errors.network'));
      return;
    }

    try {
      const response = await purchasePermanentNumber({
        purchaseCase: 2,
        serviceId: selectedServiceId,
        areaCode: selectedAreaCode || undefined,
        autoRenewEnabled,
        alwaysOn: true,
        durationKey: selectedDurationKey,
        capability: capability !== 'sms' ? capability : undefined,
      }).unwrap();

      const permanentNumber = (response.data.permanentNumber || response.data) as PermanentNumber;
      const newId = permanentNumber._id;
      navigate(`/permanent-numbers/${newId}`);
    } catch (err) {
      const apiError = err as { data?: { message?: string }; message?: string };
      const errorMessage = apiError?.data?.message || apiError?.message || t('errors.network');
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const isLoading =
    loadingTvServices ||
    loadingTvLocations ||
    loadingDurations ||
    loadingSummary ||
    isPurchasing;

  return (
    <div className="min-h-screen relative">
      <div className="container mx-auto px-4 py-16 content-overlay">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 flex items-center justify-between animate-slide-up">
            <div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-2">
                {t('permanentNumbers.purchase.title')}
              </h1>
              <p className="text-neutral-medium font-body">
                {t('permanentNumbers.purchase.subtitle')}
              </p>
            </div>
            <Link
              to="/permanent-numbers/list"
              className="px-4 py-2 bg-neutral-dark text-neutral-light rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 font-body whitespace-nowrap"
            >
              {t('permanentNumbers.list.title')}
            </Link>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-danger/20 border border-danger rounded-lg text-danger text-center font-body">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column: Services */}
            <div className="card-glass rounded-2xl shadow-2xl p-6 animate-scale-in">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-heading font-semibold text-neutral-light">
                  {t('permanentNumbers.purchase.service')}
                </h2>
                {loadingTvServices && <Loader size="sm" />}
              </div>

              {/* Search Input */}
              <div className="mb-4">
                <input
                  type="text"
                  value={serviceSearchQuery}
                  onChange={(e) => setServiceSearchQuery(e.target.value)}
                  placeholder={t('permanentNumbers.purchase.searchService')}
                  className="w-full px-4 py-2 rounded-lg border border-neutral-dark bg-brand-primary/50 text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-brand-accent transition-all duration-300 font-body"
                />
              </div>

              {tvServicesError ? (
                <p className="text-danger font-body">{t('errors.network')}</p>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
                  {services.length === 0 ? (
                    <p className="text-neutral-medium font-body text-center py-4">
                      {t('permanentNumbers.purchase.noServices')}
                    </p>
                  ) : (
                    services.map((service) => {
                      const serviceId = service.id;
                      const serviceLogo = '/logos/defo.jpg';

                      return (
                          <button
                          key={serviceId}
                          onClick={() => {
                            setSelectedServiceId(serviceId);
                            setSelectedAreaCode('');
                            setSummary(null);
                          }}
                          className={`w-full text-left p-4 rounded-xl border transition-all duration-300 ${
                            selectedServiceId === serviceId
                              ? 'border-brand-accent bg-brand-accent/10 shadow-brand-accent/20 shadow-lg'
                              : 'border-neutral-dark bg-neutral-dark/40 hover:border-brand-accent/40'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {serviceLogo ? (
                                <img
                                  src={serviceLogo}
                                  alt=""
                                  className="w-6 h-6 object-contain"
                                  onError={(e) => {
                                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              ) : null}
                              <h3 className="text-lg font-heading font-semibold text-neutral-light">
                                {service.name}
                              </h3>
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {/* Right Column: Area Codes, Duration, and Purchase */}
            <div className="space-y-6">
              {/* Area Codes */}
              <div className="card-glass rounded-2xl shadow-2xl p-6 animate-scale-in">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-heading font-semibold text-neutral-light">
                    {t('permanentNumbers.purchase.areaCode')}
                  </h2>
                  {loadingTvLocations && selectedServiceId && <Loader size="sm" />}
                </div>

                {/* Search Input */}
                {selectedServiceId && (
                  <div className="mb-4">
                    <input
                      type="text"
                      value={stateSearchQuery}
                      onChange={(e) => setStateSearchQuery(e.target.value)}
                      placeholder={t('permanentNumbers.purchase.searchState')}
                      className="w-full px-4 py-2 rounded-lg border border-neutral-dark bg-brand-primary/50 text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-brand-accent transition-all duration-300 font-body"
                    />
                  </div>
                )}

                {selectedServiceId && (
                  <p className="text-xs text-yellow-500 font-body mb-2">
                    {t('permanentNumbers.purchase.stateCostNote')}
                  </p>
                )}
                {selectedServiceId ? (
                  tvLocationsError ? (
                    <p className="text-danger font-body">{t('errors.network')}</p>
                  ) : states.length === 0 ? (
                    <p className="text-neutral-medium font-body">
                      {t('permanentNumbers.purchase.noStates')}
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                      {states.map((state) => (
                        <button
                          key={state.key}
                          onClick={() => {
                            setSelectedAreaCode(state.value);
                            setSummary(null);
                          }}
                          className={`w-full text-left p-3 rounded-xl border transition-all duration-300 ${
                            selectedAreaCode === state.value
                              ? 'border-brand-accent bg-brand-accent/10 shadow-brand-accent/20 shadow-lg'
                              : 'border-neutral-dark bg-neutral-dark/40 hover:border-brand-accent/40'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-neutral-light font-heading font-semibold">
                                {state.label}
                              </span>
                            </div>
                            <span className="text-neutral-medium text-sm font-body">
                              {state.secondary}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )
                ) : (
                  <p className="text-neutral-medium font-body">
                    {t('permanentNumbers.purchase.selectServiceFirst')}
                  </p>
                )}
              </div>

              {/* Duration and Purchase */}
              {canShowPurchasePanel && (
                <div className="card-glass rounded-2xl shadow-2xl p-6 animate-scale-in space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                    <span className="text-green-400 font-body text-sm">{t('permanentNumbers.purchase.manualRenewal')}</span>
                  </div>

                  {!isSummaryStage && (
                    <div>
                      <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-4">
                        {t('permanentNumbers.purchase.capability')}
                      </h2>
                      <div className="flex gap-3">
                        {['sms'].map((cap) => (
                          <button
                            key={cap}
                            type="button"
                            onClick={() => {
                              setCapability(cap);
                              setSummary(null);
                            }}
                            className={`px-4 py-2 rounded-lg font-heading font-semibold transition-all duration-300 ${
                              capability === cap
                                ? 'bg-brand-accent text-brand-primary'
                                : 'bg-neutral-dark text-neutral-light hover:bg-neutral-dark/80'
                            }`}
                          >
                            {t(`permanentNumbers.purchase.capability_${cap}`)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-4">
                      {t('permanentNumbers.purchase.duration')}
                    </h2>
                    <div className="flex flex-wrap gap-3">
                      {durationOptions.map((duration) => (
                        <button
                          key={duration.apiValue}
                          onClick={() => {
                            setSelectedDurationKey(duration.apiValue);
                            setSummary(null);
                          }}
                          className={`px-4 py-2 rounded-lg font-heading font-semibold transition-all duration-300 ${
                            selectedDurationKey === duration.apiValue
                              ? 'bg-brand-accent text-brand-primary'
                              : 'bg-neutral-dark text-neutral-light hover:bg-neutral-dark/80'
                          }`}
                        >
                          {duration.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {!isSummaryStage && (
                    <button
                      type="button"
                      disabled={
                        !selectedServiceId || !selectedDurationKey || loadingSummary
                      }
                      onClick={() => {
                        void (async () => {
                          if (!selectedServiceId) return;
                          try {
                            const body = {
                              serverId: SERVER_ID_TEXT_VERIFIED,
                              serviceId: selectedServiceId,
                              areaCode: selectedAreaCode || undefined,
                              autoRenewEnabled,
                              durationKey: selectedDurationKey,
                              capability: capability !== 'sms' ? capability : undefined,
                            };
                            const res = await getPermanentPurchaseSummary(body).unwrap();
                            setSummary({
                              serviceName: res.data.service.name,
                              areaCode: res.data.areaCode,
                              autoRenewEnabled: res.data.autoRenewEnabled,
                              durationLabel: res.data.duration?.label,
                              durationDays: res.data.duration?.days,
                              price: res.data.price,
                              currency: res.data.currency,
                            });
                          } catch {
                            setSummary(null);
                            setError(t('errors.network'));
                          }
                        })();
                      }}
                      className="w-full px-6 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-xl hover:bg-neutral-dark/80 transition-all duration-300 disabled:opacity-50"
                    >
                      {loadingSummary ? t('common.loading') : t('common.next')}
                    </button>
                  )}

                  {isSummaryStage && summary && (
                    <div className="p-4 rounded-lg bg-neutral-dark/40 border border-neutral-dark text-sm font-body space-y-2">
                      <p className="text-neutral-medium">{t('permanentNumbers.details.service')}: <span className="text-neutral-light">{summary.serviceName}</span></p>
                      {summary.areaCode != null && (
                        <p className="text-neutral-medium">{t('permanentNumbers.purchase.areaCode')}: <span className="text-neutral-light">{summary.areaCode}</span></p>
                      )}
                      <p className="text-neutral-medium">{t('permanentNumbers.purchase.price')}: <span className="text-brand-accent font-semibold">{summary.price.toFixed(2)} {summary.currency}</span></p>
                    </div>
                  )}

                  {/* Purchase Button */}
                  {isSummaryStage && (
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setSummary(null)}
                        className="px-6 py-3 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-xl hover:bg-neutral-dark/80 transition-all duration-300"
                      >
                        {t('common.back')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (!isLoading) {
                            void handlePurchase();
                          }
                        }}
                        disabled={isLoading || !selectedServiceId || !selectedDurationKey}
                        className="flex-1 px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-xl shadow-lg hover:shadow-xl hover:shadow-brand-accent/50 transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
                      >
                        {isPurchasing ? (
                          <>
                            <ButtonSpinner />
                            <span>{t('common.loading')}</span>
                          </>
                        ) : (
                          t('permanentNumbers.purchase.cta')
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
